import { Commands, HomePins, Results } from '@local/client-contracts';
import { KeyboardService } from '@shared/services/keyboard.service';
import { KeyName } from '@local/ts-infra';
import { HubService } from 'src/app/bar/services/hub.service';
import { CollectionContextMenuPlace, CollectionOptionsItem, ItemsMap } from '../models';

export class CollectionContextMenuBuilder {
  itemsMap: ItemsMap;
  private itemsMapHolder: CollectionOptionsItem;
  private isLauncher: boolean;
  private initPromise: Promise<void>;

  get items() {
    return this.itemsMapHolder;
  }

  constructor(
    private hubService: HubService,
    private keyboardService: KeyboardService
  ) {
    this.initPromise = this.init();
  }

  private async init() {
    this.isLauncher = await this.hubService.getIsLauncher();
    this.itemsMap = {
      'collection-view': ['copy_url', 'favorite', 'add_to_collection', 'make_a_copy', 'pin'],
      collection: ['copy_url', 'favorite', 'add_to_collection', 'make_a_copy', 'pin', 'delete'],
      'search-result': this.isLauncher
        ? ['search_in_collection', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'share', 'verification_details']
        : [
            'open_collection',
            'search_in_collection',
            'copy_url',
            'favorite',
            'add_to_collection',
            'share',
            'pin',
            'verification_details',
            'remove',
          ],
      'search-result-wiki': this.isLauncher
        ? ['search_in_wiki', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'share', 'verification_details']
        : [
            'open_collection',
            'search_in_wiki',
            'copy_url',
            'favorite',
            'add_to_collection',
            'share',
            'pin',
            'verification_details',
            'delete',
          ],
      'collection-search-result': this.isLauncher
        ? ['search_in_collection', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'share', 'remove', 'delete']
        : ['open_collection', 'search_in_collection', 'copy_url', 'favorite', 'add_to_collection', 'share', 'pin', 'remove', 'delete'],
      'search-result-view': this.isLauncher
        ? ['search_in_collection', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'verification_details']
        : [
            'open_collection',
            'search_in_collection',
            'copy_url',
            'favorite',
            'add_to_collection',
            'make_a_copy',
            'pin',
            'verification_details',
          ],
      'search-result-view-wiki': this.isLauncher
        ? ['search_in_wiki', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'verification_details']
        : ['open_collection', 'search_in_wiki', 'copy_url', 'favorite', 'add_to_collection', 'make_a_copy', 'pin', 'verification_details'],
      'collection-search-result-view': this.isLauncher
        ? ['search_in_collection', 'open_collection', 'copy_url', 'favorite', 'add_to_collection', 'delete']
        : ['open_collection', 'search_in_collection', 'copy_url', 'favorite', 'add_to_collection', 'pin', 'delete'],
      card: [
        'open_collection',
        'copy_url',
        'favorite',
        'add_to_collection',
        'make_a_copy',
        'share',
        'pin',
        'verification_details',
        'delete',
      ],
      'card-view': ['open_collection', 'copy_url', 'favorite', 'add_to_collection', 'make_a_copy', 'pin', 'verification_details'],
      list: [
        'open_collection',
        'copy_url',

        'favorite',
        'add_to_collection',
        'make_a_copy',
        'share',
        'pin',
        'verification_details',
        'delete',
      ],
      'list-view': ['open_collection', 'copy_url', 'favorite', 'add_to_collection', 'make_a_copy', 'pin', 'verification_details'],
      'static-collection': ['paste'],
      'split-button': ['save_as_new'],
      'wiki-card-draft': ['copy_url', 'view_publish', 'delete_draft'],
      'wiki-card-edit': ['copy_url', 'move_card', 'make_a_copy', 'verify', 'unverify', 'request_verification', 'version_history', 'delete'],
      'wiki-card-view': ['copy_url', 'make_a_copy', 'verify', 'unverify', 'request_verification'],
      'wiki-card-inline': ['copy_url', 'make_a_copy', 'verify', 'unverify', 'request_verification', 'delete'],
      widget: [
        'open_collection',
        'copy_url',
        'change_layout',
        'unpin',
        'pin',
        'share',
        'add_to_collection',
        'favorite',
        'verification_details',
      ],
      'widget-view': ['open_collection', 'copy_url', 'change_layout', 'favorite', 'add_to_collection', 'pin'],
      'gallery-card': this.isLauncher
        ? ['open_collection', 'edit', 'copy_url', 'favorite', 'add_to_collection', 'share', 'delete']
        : ['open_collection', 'copy_url', 'favorite', 'add_to_collection', 'share', 'pin', 'delete'],
      'gallery-card-view': this.isLauncher
        ? ['open_collection', 'edit', 'copy_url', 'favorite', 'add_to_collection', 'delete']
        : ['open_collection', 'copy_url', 'favorite', 'add_to_collection', 'pin', 'delete'],
    };
  }

  private getPinText(pinBy: HomePins.HomePin[]) {
    return pinBy?.length > 0 ? 'Manage Pins' : 'Pin';
  }

  async initItems(isFavorite?: boolean, pinBy?: HomePins.HomePin[], layoutMode?: Results.LayoutType): Promise<void> {
    await this.initPromise;
    const shortcuts: any = await this.keyboardService.loadShortcuts<any>(['_collection']);
    this.itemsMapHolder = {
      open_collection: {
        id: 'open_collection',
        text: 'Open',
        command: { type: 'open-collection' },
        shortcut: !this.isLauncher ? <KeyName[]>shortcuts?.open.map((k) => k.toLowerCase()) : null,
        icon: { type: 'font-icon', value: 'icon-launch' },
      },
      search_in_collection: {
        id: 'search_in_collection',
        text: 'Search in Collection',
        command: { type: 'search-in-collection' },
        shortcut: this.isLauncher ? <KeyName[]>shortcuts?.open.map((k) => k.toLowerCase()) : null,
        icon: { type: 'font-icon', value: 'icon-search-icon-fixed' },
      },
      search_in_wiki: {
        id: 'search_in_collection',
        text: 'Search in Wiki',
        command: { type: 'search-in-collection' },
        shortcut: this.isLauncher ? <KeyName[]>shortcuts?.open.map((k) => k.toLowerCase()) : null,
        icon: { type: 'font-icon', value: 'icon-search-icon-fixed' },
      },
      copy_url: {
        id: 'copy_clipboard',
        text: 'Copy URL',
        command: { type: 'copy-url' },
        icon: { type: 'font-icon', value: 'icon-link2' },
        shortcut: <KeyName[]>shortcuts?.copyUrl.map((k) => k.toLowerCase()),
      },
      favorite: {
        id: 'favorite',
        text: `${isFavorite ? 'Remove from' : 'Add to'} Favorites`,
        command: { type: 'change-favorite-status', status: isFavorite ? 'remove' : 'add' } as Commands.ChangeFavoriteStatus,
        shortcut: shortcuts?.favorite as Array<KeyName>,
        icon: { type: 'font-icon', value: 'icon-star' },
      },
      make_a_copy: {
        id: 'make_a_copy',
        text: 'Make a copy',
        command: { type: 'make_a_copy' },
        shortcut: <KeyName[]>shortcuts?.duplicate.map((k) => k.toLowerCase()),
        icon: { type: 'font-icon', value: 'icon-duplicate' },
      },
      save_as_new: {
        id: 'save_as_new',
        text: 'Save as new',
        command: { type: 'save_as_new' },
        shortcut: <KeyName[]>shortcuts?.duplicate.map((k) => k.toLowerCase()),
        icon: { type: 'font-icon', value: 'icon-duplicate' },
      },
      remove: {
        id: 'remove',
        text: 'Remove',
        command: { type: 'dynamicCommands', value: 'remove' } as Commands.DynamicCommand,
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
      delete: {
        id: 'delete',
        text: 'Delete',
        command: { type: 'delete' },
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
      paste: {
        id: 'paste',
        text: 'Paste',
        command: { type: 'paste' },
      },
      edit: {
        id: 'edit',
        text: 'Edit',
        command: { type: 'edit-collection' },
        icon: { type: 'font-icon', value: 'icon-edit' },
      },
      add_to_collection: {
        id: 'add_to_collection',
        text: 'Add to Collection',
        command: { type: 'add-to-collection' },
        icon: { type: 'font-icon', value: 'icon-collection' },
      },
      save: {
        id: 'save',
        text: 'Save',
        command: { type: 'save' },
        icon: { type: 'font-icon', value: 'icon-floppy-disk' },
        shortcut: <KeyName[]>shortcuts?.save.map((k) => k.toLowerCase()),
      },
      pin: {
        id: 'pin',
        text: this.getPinText(pinBy),
        command: { type: 'pin' },
        icon: { type: 'font-icon', value: 'icon-windows-pin' },
      },
      unpin: {
        id: 'unpin',
        text: 'Unpin',
        command: { type: 'unpin-collection' },
        icon: { type: 'font-icon', value: 'icon-windows-unpin' },
      },
      share: {
        id: 'share',
        text: 'Share',
        command: { type: 'share-collection' },
        icon: { type: 'font-icon', value: 'icon-share2' },
      },
      change_layout: {
        id: 'change_layout',
        text: `Change to ${layoutMode === 'gallery' ? 'List' : 'Gallery'} View`,
        command: { type: 'change-layout', value: layoutMode === 'gallery' ? 'list' : 'gallery' } as Commands.DynamicCommand,
        icon: { type: 'font-icon', value: layoutMode === 'list' ? 'icon-gallery-view' : 'icon-list-view' },
      },
      verify: {
        id: 'verify',
        text: 'Verify',
        command: { type: 'verify' },
        icon: { type: 'font-icon', value: 'icon-verify' },
      },
      unverify: {
        id: 'unverify',
        text: 'Unverify',
        command: { type: 'unverify' },
        icon: { type: 'font-icon', value: 'icon-unverify' },
      },
      request_verification: {
        id: 'request_verification',
        text: 'Request verification',
        command: { type: 'request-verification' },
        icon: { type: 'font-icon', value: 'icon-request' },
      },
      verification_details: {
        id: 'verification_details',
        text: 'Verification details',
        command: { type: 'verification-details' },
        icon: { type: 'font-icon', value: 'icon-info' },
      },
      move_card: {
        id: 'move-card',
        text: 'Move to...',
        icon: { type: 'font-icon', value: 'icon-exit' },
        command: { type: 'move-card' },
      },
      delete_draft: {
        id: 'delete-draft',
        text: 'Delete draft',
        command: { type: 'delete-draft' },
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
      view_publish: {
        id: 'view-publish',
        text: 'View published card',
        command: { type: 'view-publish' },
        icon: { type: 'font-icon', value: 'icon-launch' },
      },
      version_history: {
        id: 'version-history',
        text: 'Version history',
        command: { type: 'version-history' },
        icon: { type: 'font-icon', value: 'icon-clock' },
      },
    };
  }

  async buildItemsForView(
    viewMode: CollectionContextMenuPlace,
    withShortcut = true,
    removeItems: string[] = [],
    disabledItems: string[] = []
  ) {
    await this.initPromise;
    const items = [];
    this.itemsMap[viewMode].filter((id) => !removeItems.includes(id)).forEach((id) => items.push(this.itemsMapHolder[id]));
    if (disabledItems.length > 0) {
      disabledItems.forEach((disableItem) => {
        const find = items.find((item) => item.id === disableItem);
        if (find) {
          find.disabled = true;
        }
      });
    }
    if (withShortcut) {
      return items;
    }
    return items.map((item) => ({ ...item, shortcut: null }));
  }
}
